import { GameObject, Vector2, Rectangle } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';
import { MazeSystem } from '../systems/MazeSystem';
import { PathFinding } from '../systems/PathFinding';

export enum GhostType {
  BLINKY = 'blinky',  // Red - Aggressive chaser
  PINKY = 'pinky',    // Pink - Ambusher
  INKY = 'inky',      // Blue - Unpredictable
  CLYDE = 'clyde'     // Orange - Shy
}

export enum GhostMode {
  CHASE = 'chase',
  SCATTER = 'scatter',
  FRIGHTENED = 'frightened',
  EATEN = 'eaten',
  IN_HOUSE = 'in_house'
}

export class Ghost implements GameObject {
  public id: string;
  public position: Vector2;
  public velocity: Vector2;
  public size: Vector2;
  public active: boolean = true;

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private mazeSystem: MazeSystem;
  private pathFinding: PathFinding;
  
  // Ghost properties
  private type: GhostType;
  private mode: GhostMode = GhostMode.IN_HOUSE;
  private color: string = '#FFFFFF';
  private scatterTarget: Vector2 = { x: 0, y: 0 };
  
  // Movement
  private gridPosition: Vector2;
  private targetPosition: Vector2;
  private currentDirection: Vector2 = { x: 0, y: 0 };
  private isMoving: boolean = false;
  private moveSpeed: number = 60; // pixels per second
  private frightenedSpeed: number = 40;
  private tileSize: number = 16;
  
  // AI behavior
  private modeTimer: number = 0;
  private frightenedTimer: number = 0;
  private flashTimer: number = 0;
  private houseExitDelay: number = 0;
  
  // Animation
  private animationTime: number = 0;
  private animationFrame: number = 0;
  
  // Target tracking
  private playerGridPosition: Vector2 = { x: 0, y: 0 };
  private targetGridPosition: Vector2 = { x: 0, y: 0 };

  constructor(
    id: string,
    type: GhostType,
    gridX: number,
    gridY: number,
    renderManager: RenderManager,
    audioManager: AudioManager,
    mazeSystem: MazeSystem,
    pathFinding: PathFinding
  ) {
    this.id = id;
    this.type = type;
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.mazeSystem = mazeSystem;
    this.pathFinding = pathFinding;
    this.tileSize = mazeSystem.getTileSize();
    
    // Set ghost properties based on type
    this.setupGhostProperties();
    
    // Initialize position
    this.gridPosition = { x: gridX, y: gridY };
    this.position = mazeSystem.gridToScreen(gridX, gridY);
    this.targetPosition = { ...this.position };
    this.velocity = { x: 0, y: 0 };
    this.size = { x: this.tileSize, y: this.tileSize };
    
    // Set exit delay based on ghost type
    this.setHouseExitDelay();
  }

  private setupGhostProperties(): void {
    switch (this.type) {
      case GhostType.BLINKY:
        this.color = '#883932'; // Red
        this.scatterTarget = { x: 23, y: 0 }; // Top-right corner
        break;
      case GhostType.PINKY:
        this.color = '#8B3F96'; // Pink/Purple
        this.scatterTarget = { x: 1, y: 0 }; // Top-left corner
        break;
      case GhostType.INKY:
        this.color = '#62D0FE'; // Light Blue
        this.scatterTarget = { x: 23, y: 30 }; // Bottom-right corner
        break;
      case GhostType.CLYDE:
        this.color = '#BFCE72'; // Yellow/Orange
        this.scatterTarget = { x: 1, y: 30 }; // Bottom-left corner
        break;
    }
  }

  private setHouseExitDelay(): void {
    switch (this.type) {
      case GhostType.BLINKY:
        this.houseExitDelay = 0; // Exits immediately
        this.mode = GhostMode.CHASE;
        break;
      case GhostType.PINKY:
        this.houseExitDelay = 2; // 2 seconds delay
        break;
      case GhostType.INKY:
        this.houseExitDelay = 4; // 4 seconds delay
        break;
      case GhostType.CLYDE:
        this.houseExitDelay = 6; // 6 seconds delay
        break;
    }
  }

  public update(deltaTime: number): void {
    // Update animation
    this.updateAnimation(deltaTime);
    
    // Update mode timers
    this.updateTimers(deltaTime);
    
    // Update AI behavior
    this.updateAI();
    
    // Handle movement
    this.updateMovement(deltaTime);
  }

  private updateAnimation(deltaTime: number): void {
    this.animationTime += deltaTime * 4; // 4 FPS animation
    this.animationFrame = Math.floor(this.animationTime) % 2;
  }

  private updateTimers(deltaTime: number): void {
    // House exit timer
    if (this.mode === GhostMode.IN_HOUSE && this.houseExitDelay > 0) {
      this.houseExitDelay -= deltaTime;
      if (this.houseExitDelay <= 0) {
        this.mode = GhostMode.CHASE;
      }
    }
    
    // Frightened mode timer
    if (this.mode === GhostMode.FRIGHTENED) {
      this.frightenedTimer -= deltaTime;
      this.flashTimer += deltaTime;
      
      if (this.frightenedTimer <= 0) {
        this.mode = GhostMode.CHASE;
        this.frightenedTimer = 0;
        this.flashTimer = 0;
      }
    }
    
    // Mode switching timer
    this.modeTimer += deltaTime;
  }

  private updateAI(): void {
    if (this.mode === GhostMode.EATEN) {
      // Return to ghost house when eaten
      if (!this.isMoving) {
        const housePosition = { x: 12, y: 10 }; // Ghost house center
        const direction = this.pathFinding.getDirectionToTarget(this.gridPosition, housePosition);
        if (direction.x !== 0 || direction.y !== 0) {
          this.startMoving(direction);
        }
        
        // If reached house, resurrect
        if (this.gridPosition.x === housePosition.x && this.gridPosition.y === housePosition.y) {
          this.mode = GhostMode.IN_HOUSE;
          this.setHouseExitDelay();
        }
      }
      return;
    }
    
    // Special handling for ghosts in house
    if (this.mode === GhostMode.IN_HOUSE) {
      // Move up to exit the house
      if (!this.isMoving) {
        const exitDirection = { x: 0, y: -1 }; // Move up to exit
        this.startMoving(exitDirection);
      }
      return;
    }
    
    // Calculate target based on current mode and ghost personality
    this.calculateTarget();
    
    // Get direction towards target
    if (!this.isMoving) {
      const direction = this.getNextDirection();
      if (direction.x !== 0 || direction.y !== 0) {
        this.startMoving(direction);
      }
    }
  }

  private calculateTarget(): void {
    switch (this.mode) {
      case GhostMode.CHASE:
        this.targetGridPosition = this.getChaseTarget();
        break;
      case GhostMode.SCATTER:
        this.targetGridPosition = this.scatterTarget;
        break;
      case GhostMode.FRIGHTENED:
        // No specific target, move randomly
        this.targetGridPosition = this.gridPosition;
        break;
    }
  }

  private getChaseTarget(): Vector2 {
    switch (this.type) {
      case GhostType.BLINKY:
        // Directly chase the player
        return this.playerGridPosition;
        
      case GhostType.PINKY:
        // Target 4 tiles ahead of player's direction
        // This is a simplified version - in original Pac-Man it's more complex
        return {
          x: Math.max(0, Math.min(this.mazeSystem.getMazeWidth() - 1, this.playerGridPosition.x + 4)),
          y: Math.max(0, Math.min(this.mazeSystem.getMazeHeight() - 1, this.playerGridPosition.y))
        };
        
      case GhostType.INKY:
        // Complex behavior: target point relative to Blinky's position and player
        // Simplified: target point opposite to Blinky relative to player
        const offset = Math.random() > 0.5 ? 4 : -4;
        return {
          x: Math.max(0, Math.min(this.mazeSystem.getMazeWidth() - 1, this.playerGridPosition.x + offset)),
          y: Math.max(0, Math.min(this.mazeSystem.getMazeHeight() - 1, this.playerGridPosition.y + offset))
        };
        
      case GhostType.CLYDE:
        // Chase player when far away, retreat to scatter corner when close
        const distance = this.pathFinding.getShortestDistance(this.gridPosition, this.playerGridPosition);
        if (distance > 8) {
          return this.playerGridPosition;
        } else {
          return this.scatterTarget;
        }
        
      default:
        return this.playerGridPosition;
    }
  }

  private getNextDirection(): Vector2 {
    if (this.mode === GhostMode.FRIGHTENED) {
      // Move randomly when frightened, but avoid reversing direction
      return this.pathFinding.getRandomDirection(this.gridPosition, this.currentDirection);
    } else {
      // Use pathfinding to move towards target
      return this.pathFinding.getDirectionToTarget(this.gridPosition, this.targetGridPosition);
    }
  }

  private startMoving(direction: Vector2): void {
    const nextGridPos = {
      x: this.gridPosition.x + direction.x,
      y: this.gridPosition.y + direction.y
    };
    
    // Handle tunnel wrapping
    const wrappedPos = this.mazeSystem.handleTunnelWrap(nextGridPos.x, nextGridPos.y);
    
    if (this.mazeSystem.isWalkable(wrappedPos.x, wrappedPos.y)) {
      this.gridPosition = wrappedPos;
      this.targetPosition = this.mazeSystem.gridToScreen(wrappedPos.x, wrappedPos.y);
      this.currentDirection = direction;
      
      // Set velocity
      const moveDirection = {
        x: this.targetPosition.x - this.position.x,
        y: this.targetPosition.y - this.position.y
      };
      const distance = Math.sqrt(moveDirection.x * moveDirection.x + moveDirection.y * moveDirection.y);
      
      if (distance > 0) {
        const speed = this.mode === GhostMode.FRIGHTENED ? this.frightenedSpeed : this.moveSpeed;
        this.velocity = {
          x: (moveDirection.x / distance) * speed,
          y: (moveDirection.y / distance) * speed
        };
      }
      
      this.isMoving = true;
    } else {
      // If can't move in desired direction, try a random valid direction
      const validDirections = this.mazeSystem.getValidDirections(this.gridPosition.x, this.gridPosition.y);
      if (validDirections.length > 0) {
        const randomDir = validDirections[Math.floor(Math.random() * validDirections.length)];
        this.startMoving(randomDir);
      }
    }
  }

  private updateMovement(deltaTime: number): void {
    if (this.isMoving) {
      this.position.x += this.velocity.x * deltaTime;
      this.position.y += this.velocity.y * deltaTime;
      
      // Check if reached target
      const dx = this.targetPosition.x - this.position.x;
      const dy = this.targetPosition.y - this.position.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < 2) {
        // Snap to grid
        this.position = { ...this.targetPosition };
        this.velocity = { x: 0, y: 0 };
        this.isMoving = false;
      }
    }
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;

    const centerX = this.position.x + this.size.x / 2;
    const centerY = this.position.y + this.size.y / 2;
    
    if (this.mode === GhostMode.EATEN) {
      this.drawEatenGhost(centerX, centerY);
    } else if (this.mode === GhostMode.FRIGHTENED) {
      this.drawFrightenedGhost(centerX, centerY);
    } else {
      this.drawNormalGhost(centerX, centerY);
    }
  }

  private drawNormalGhost(centerX: number, centerY: number): void {
    const size = 6;
    
    // Ghost body (rounded rectangle)
    this.renderManager.fillRect(centerX - size, centerY - size, size * 2, size * 1.5, this.color);
    
    // Ghost bottom (wavy)
    const waveOffset = this.animationFrame * 2;
    for (let i = 0; i < 4; i++) {
      const x = centerX - size + i * 3;
      const y = centerY + size * 0.5;
      const height = (i + waveOffset) % 2 === 0 ? 3 : 1;
      this.renderManager.fillRect(x, y - height, 3, height, this.color);
    }
    
    // Eyes
    this.renderManager.drawCircle(centerX - 2, centerY - 2, 1, true, '#FFFFFF');
    this.renderManager.drawCircle(centerX + 2, centerY - 2, 1, true, '#FFFFFF');
    
    // Pupils (looking towards movement direction)
    let pupilOffsetX = 0;
    let pupilOffsetY = 0;
    
    if (this.currentDirection.x > 0) pupilOffsetX = 0.5;
    else if (this.currentDirection.x < 0) pupilOffsetX = -0.5;
    if (this.currentDirection.y > 0) pupilOffsetY = 0.5;
    else if (this.currentDirection.y < 0) pupilOffsetY = -0.5;
    
    this.renderManager.drawPixel(centerX - 2 + pupilOffsetX, centerY - 2 + pupilOffsetY, '#000000');
    this.renderManager.drawPixel(centerX + 2 + pupilOffsetX, centerY - 2 + pupilOffsetY, '#000000');
  }

  private drawFrightenedGhost(centerX: number, centerY: number): void {
    const size = 6;
    
    // Determine flash color
    let bodyColor = '#40318D'; // Dark blue
    let eyeColor = '#FFFFFF';
    
    // Flash white when frightened time is almost up
    if (this.frightenedTimer < 2 && Math.floor(this.flashTimer * 8) % 2 === 0) {
      bodyColor = '#FFFFFF';
      eyeColor = '#883932'; // Red eyes when flashing
    }
    
    // Ghost body
    this.renderManager.fillRect(centerX - size, centerY - size, size * 2, size * 1.5, bodyColor);
    
    // Ghost bottom (wavy)
    const waveOffset = this.animationFrame * 2;
    for (let i = 0; i < 4; i++) {
      const x = centerX - size + i * 3;
      const y = centerY + size * 0.5;
      const height = (i + waveOffset) % 2 === 0 ? 3 : 1;
      this.renderManager.fillRect(x, y - height, 3, height, bodyColor);
    }
    
    // Frightened face
    this.renderManager.drawCircle(centerX - 2, centerY - 2, 1, true, eyeColor);
    this.renderManager.drawCircle(centerX + 2, centerY - 2, 1, true, eyeColor);
    
    // Worried mouth
    this.renderManager.drawPixel(centerX - 1, centerY + 1, eyeColor);
    this.renderManager.drawPixel(centerX, centerY + 2, eyeColor);
    this.renderManager.drawPixel(centerX + 1, centerY + 1, eyeColor);
  }

  private drawEatenGhost(centerX: number, centerY: number): void {
    // Draw only eyes for eaten ghost
    this.renderManager.drawCircle(centerX - 2, centerY - 2, 2, true, '#FFFFFF');
    this.renderManager.drawCircle(centerX + 2, centerY - 2, 2, true, '#FFFFFF');
    
    // Pupils looking towards movement direction
    let pupilOffsetX = 0;
    let pupilOffsetY = 0;
    
    if (this.currentDirection.x > 0) pupilOffsetX = 1;
    else if (this.currentDirection.x < 0) pupilOffsetX = -1;
    if (this.currentDirection.y > 0) pupilOffsetY = 1;
    else if (this.currentDirection.y < 0) pupilOffsetY = -1;
    
    this.renderManager.drawCircle(centerX - 2 + pupilOffsetX, centerY - 2 + pupilOffsetY, 1, true, '#000000');
    this.renderManager.drawCircle(centerX + 2 + pupilOffsetX, centerY - 2 + pupilOffsetY, 1, true, '#000000');
  }

  // Public methods for game logic
  public setPlayerPosition(position: Vector2): void {
    this.playerGridPosition = position;
  }

  public setMode(mode: GhostMode, duration?: number): void {
    this.mode = mode;
    
    if (mode === GhostMode.FRIGHTENED && duration) {
      this.frightenedTimer = duration;
      this.flashTimer = 0;
    }
  }

  public getMode(): GhostMode {
    return this.mode;
  }

  public getGridPosition(): Vector2 {
    return { ...this.gridPosition };
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.size.x,
      height: this.size.y
    };
  }

  public isVulnerable(): boolean {
    return this.mode === GhostMode.FRIGHTENED;
  }

  public getType(): GhostType {
    return this.type;
  }

  public destroy(): void {
    this.active = false;
  }

  public reset(gridX: number, gridY: number): void {
    this.gridPosition = { x: gridX, y: gridY };
    this.position = this.mazeSystem.gridToScreen(gridX, gridY);
    this.targetPosition = { ...this.position };
    this.velocity = { x: 0, y: 0 };
    this.currentDirection = { x: 0, y: 0 };
    this.isMoving = false;
    this.active = true;
    this.mode = this.type === GhostType.BLINKY ? GhostMode.CHASE : GhostMode.IN_HOUSE;
    this.setHouseExitDelay();
    this.modeTimer = 0;
    this.frightenedTimer = 0;
    this.flashTimer = 0;
    this.animationTime = 0;
    this.animationFrame = 0;
  }

  public eatGhost(): number {
    // Return points for eating this ghost
    this.mode = GhostMode.EATEN;
    this.audioManager.playPowerUp();
    
    // Different point values based on how many ghosts eaten in succession
    return 200; // Base points, can be multiplied by game logic
  }
}